import { AlertTriangle, BookOpen, MoreHorizontal } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { CarrierPocketbook } from '@/components/carrier-directory/CarrierPocketbook';
import {
  LegacyLOAChaserSection,
  LOAChaseStatusCard,
} from '@/components/LOAChaser';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCarriers } from '@/hooks/useCarriers';
import { cn } from '@/lib/utils';
import {
  useMergeCoveragesMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import { usePropertiesWithOwnersQuery } from '@/services/api/subjectsService';
import { useTaskDeliverablesListQuery } from '@/services/api/deliverablesServicev2';
import {
  AdjusterDetail,
  ContactHandleSchema,
  CoverageCategory,
  CoverageDataSourceInfo,
  CoverageLiabilityStatus,
  CoverageSchema,
  CoverageStatus,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';

import { CoverageLiabilityStatusBadge } from '../CoverageLiabilityStatusBadge';
import { benefitTypeLabels, FIRST_PARTY_RELEVANT_BENEFIT_TYPES, identifierTypeLabels } from './constants';
import { CoverageStepRow, useCoverageStepsCompletion } from './CoverageStepRow';
import { DataSourceBadge } from './DataSourceBadge';
import { formatLimitValue } from './utils';
import { getEnrichmentBadgeProps } from './utils/cacheUtils';


interface CoverageCardProps {
  coverage: CoverageSchema;
  onClick?: () => void;
  className?: string;
  matterId?: string; // when provided, shows inline steps panel in the card
  onDelete?: (coverageId: string) => void;
  onMerge?: (destinationId: string, sourceId: string) => void;
  allCoverages?: CoverageSchema[];
}

const getPolicyTypeText = (policyType: PolicyType | null) => {
  switch (policyType) {
    case PolicyType.AUTO:
      return 'AUTO';
    case PolicyType.HEALTH:
      return 'HEALTH';
    case PolicyType.HOMEOWNERS:
      return 'HOME';
    case PolicyType.COMMERCIAL:
      return 'COMMERCIAL';
    case PolicyType.UMBRELLA:
      return 'UMBRELLA';
    case PolicyType.GENERAL_LIABILITY:
      return 'GL';
    default:
      return 'POLICY';
  }
};

const getPrimaryIdentifierWithType = (coverage: CoverageSchema) => {
  if (
    !coverage.policy_identifiers ||
    coverage.policy_identifiers.length === 0
  ) {
    return null;
  }
  const policyNumber = coverage.policy_identifiers.find(
    (id) => id.identifier_type === IdentifierType.POLICY_NUMBER,
  );
  const identifier = policyNumber || coverage.policy_identifiers[0];
  return {
    label: identifierTypeLabels[identifier.identifier_type] || 'Identifier',
    value: identifier.identifier_value,
  };
};

const getPrimaryClaimNumber = (
  coverage: CoverageSchema,
): {
  claimNumber: string;
  source: CoverageDataSourceInfo | null | undefined;
} | null => {
  const { claims } = coverage;
  if (!claims || claims.length === 0) return null;
  const withIdentifier = claims.find((c) => Boolean(c.claim_identifier));
  if (!withIdentifier?.claim_identifier) return null;
  return {
    claimNumber: withIdentifier.claim_identifier,
    source: withIdentifier.source,
  };
};

const formatContactValue = (value: string | null | undefined, type: string) => {
  if (!value) return '';

  if (type === 'phone' || type === 'fax') {
    // Format phone numbers for better readability
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
  }
  return value;
};

const getContactDisplayLabel = (contact: ContactHandleSchema): string => {
  if (contact.name) {
    return contact.name;
  }

  const contactType = contact.handle_type?.toLowerCase() || 'contact';
  switch (contactType) {
    case 'phone':
      return 'Phone';
    case 'email':
      return 'Email';
    case 'website':
      return 'Website';
    case 'fax':
      return 'Fax';
    default:
      return contactType.charAt(0).toUpperCase() + contactType.slice(1);
  }
};

const renderCarrierContacts = (contacts: ContactHandleSchema[]) => {
  if (!contacts || contacts.length === 0) return null;

  return contacts.map((contact, index) => {
    const contactType = contact.handle_type?.toLowerCase() || 'contact';
    const displayLabel = getContactDisplayLabel(contact);

    return (
      <div key={`contact-${index}`} className="text-[12px] text-zinc-700">
        <span className="font-medium text-zinc-800">{displayLabel}:</span>{' '}
        <span className="break-all">
          {formatContactValue(contact.value, contactType)}
        </span>
      </div>
    );
  });
};

const renderAdjusters = (adjusters: AdjusterDetail[] | null | undefined) => {
  if (!adjusters || adjusters.length === 0) return null;

  return adjusters.map((adjuster, index) => {
    const benefitLabel = adjuster.benefit_type
      ? benefitTypeLabels[adjuster.benefit_type]
      : null;

    // Get up to 3 handles
    const handles = adjuster.handles?.slice(0, 3) || [];

    return (
      <div key={`adjuster-${index}`} className="text-[12px] text-zinc-700">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium text-zinc-800">Adjuster:</span>{' '}
          <span className="break-all">
            {adjuster.name || 'Unknown'}
            {benefitLabel && (
              <span className="text-zinc-500 ml-1">({benefitLabel})</span>
            )}
          </span>
          <DataSourceBadge source={adjuster.source} className="ml-0.5" />
        </div>
        {handles.length > 0 && (
          <div className="mt-0.5 space-y-0.5">
            {handles.map((handle, handleIndex) => {
              const handleType = handle.handle_type?.toLowerCase() || 'contact';
              const label = getContactDisplayLabel(handle);
              return (
                <div
                  key={`handle-${handleIndex}`}
                  className="text-[11px] text-zinc-600"
                >
                  <span className="text-zinc-500">{label}:</span>{' '}
                  {formatContactValue(handle.value, handleType)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  });
};

const getStatusButtonClasses = (status: CoverageStatus | null): string => {
  switch (status) {
    case CoverageStatus.APPROVED:
      return 'bg-green-100 text-green-700 border-green-200';
    case CoverageStatus.REJECTED:
      return 'bg-red-100 text-red-700 border-red-200';
    case CoverageStatus.NOT_APPLICABLE:
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-amber-100 text-amber-700 border-amber-200';
  }
};

const getStatusLabel = (status: CoverageStatus | null): string => {
  switch (status) {
    case CoverageStatus.APPROVED:
      return 'Approved';
    case CoverageStatus.REJECTED:
      return 'Rejected';
    case CoverageStatus.NOT_APPLICABLE:
      return 'Not Applicable';
    default:
      return 'Needs Approval';
  }
};

const renderPolicyLimits = (coverage: CoverageSchema, onClick?: () => void) => {
  if (!coverage.benefits || coverage.benefits.length === 0) return null;

  const isFirstParty = coverage.category === CoverageCategory.FIRST_PARTY;

  // Get benefits with limitations, filtering to UM/UIM/PIP/MedPay for 1P coverages
  const benefitsWithLimits = coverage.benefits.filter(
    (benefit) =>
      benefit.limitations &&
      Object.keys(benefit.limitations).length > 0 &&
      (!isFirstParty || FIRST_PARTY_RELEVANT_BENEFIT_TYPES.has(benefit.benefit_type)),
  );

  if (benefitsWithLimits.length === 0) return null;

  const visibleBenefits = benefitsWithLimits.slice(0, 2);
  const hasMoreBenefits = benefitsWithLimits.length > 2;

  return (
    <div className="border-t border-zinc-100 pt-2.5 space-y-2">
      <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">
        Policy Limits
      </div>
      {visibleBenefits.map((benefit, index) => (
        <div key={index} className="flex gap-2 text-[12px]">
          {/* Benefit type on the left */}
          <div className="font-medium text-zinc-800 flex-shrink-0 w-20">
            {benefitTypeLabels[benefit.benefit_type]}
          </div>
          {/* Limitations on the right */}
          <div className="flex-1 space-y-0.5">
            {benefit.limitations &&
              Object.entries(benefit.limitations).map(([key, value]) => (
                <div
                  key={key}
                  className="flex items-baseline gap-1.5 text-[11px]"
                >
                  <span className="capitalize text-zinc-500 min-w-16">
                    {key.toLowerCase()}:
                  </span>
                  <span className="font-semibold text-zinc-900 tabular-nums ml-auto">
                    ${formatLimitValue(value)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ))}
      {hasMoreBenefits && onClick && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="text-[11px] text-violet-600 hover:text-violet-700 hover:underline font-medium"
        >
          View more
        </button>
      )}
    </div>
  );
};

export const CoverageCard = ({
  coverage,
  onClick,
  className,
  matterId,
  onDelete,
  onMerge,
  allCoverages,
}: CoverageCardProps) => {
  const navigate = useNavigate();
  const [mergeCoverages] = useMergeCoveragesMutation();
  const [updateCoverage] = useUpdateCoverageMutation();
  const { carriers } = useCarriers();
  // Resolve linked division name if selected
  const carrierIdForDivision = coverage.carrier_v2_id || '';
  const divisionId = coverage.carrier_division_id || null;
  const directoryCarrier = carriers.find(
    (carrier) => carrier.id === carrierIdForDivision,
  );
  const coverageCarrierName = coverage.carrier?.name ?? null;
  const hasDirectoryCarrier = Boolean(carrierIdForDivision);
  const normalizeName = (value: string | null | undefined): string | null => {
    if (!value) return null;
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
  };
  const directoryCarrierSlug = hasDirectoryCarrier
    ? normalizeName(directoryCarrier?.name)
    : null;
  const coverageCarrierSlug = normalizeName(coverageCarrierName);
  const carrierNameMismatch =
    hasDirectoryCarrier &&
    directoryCarrierSlug &&
    coverageCarrierSlug &&
    directoryCarrierSlug !== coverageCarrierSlug;

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('[CoverageCard] carrier division linkage', {
      coverageId: coverage.id,
      carrierIdForDivision,
      divisionId,
    });
  }, [carrierIdForDivision, coverage.id, divisionId]);

  const primaryIdentifier = getPrimaryIdentifierWithType(coverage);
  const primaryClaim = getPrimaryClaimNumber(coverage);
  const policyTypeText = getPolicyTypeText(coverage.policy_type);
  const categoryLabel = (() => {
    switch (coverage.category) {
      case CoverageCategory.FIRST_PARTY:
        return '1P';
      case CoverageCategory.THIRD_PARTY:
        return '3P';
      case CoverageCategory.FIRST_PARTY_HEALTH:
        return 'Health';
      case CoverageCategory.NOT_LIABLE_INVOLVED_PARTY:
        return 'Not-Liable';
      case CoverageCategory.PROPERTY:
        return 'Premises';
      default:
        return 'Unclear';
    }
  })();

  // Check if this is a premises liability coverage (property)
  const isPremisesCategory = coverage.category === CoverageCategory.PROPERTY;

  // Fetch property data to show property_name (not carrier.name) for premises
  const { data: propertyWithOwners } = usePropertiesWithOwnersQuery(
    {
      id: coverage.property_id ?? '',
      matterId: matterId ?? '',
    },
    {
      skip: !coverage.property_id || !isPremisesCategory || !matterId,
    },
  );

  // For premises, show property_name as the primary display (not carrier.name)
  const premisesDisplayName = React.useMemo(() => {
    if (!isPremisesCategory) return null;
    if (propertyWithOwners?.property_name) {
      return propertyWithOwners.property_name;
    }
    if (coverage.named_insured && coverage.named_insured.length > 0) {
      return coverage.named_insured[0];
    }
    return 'Premises Coverage';
  }, [isPremisesCategory, propertyWithOwners?.property_name, coverage.named_insured]);

  const showStepsPanel = Boolean(matterId);

  // Check if LOA Chaser should be shown
  const { canShowLOAChaser, lorTaskId } = useCoverageStepsCompletion(
    matterId || '',
    coverage,
  );

  // Fetch deliverables for the Send LOR task to find the LOR email thread
  const { data: lorDeliverables } = useTaskDeliverablesListQuery(
    {
      matterId: matterId ?? '',
      taskId: lorTaskId ?? '',
    },
    { skip: !matterId || !lorTaskId || !canShowLOAChaser },
  );

  // Find the sent LOR email thread from deliverable matches
  const suggestedLorThreadId = useMemo(() => {
    if (!lorDeliverables || lorDeliverables.length === 0) return null;

    // Find the "Sent Letter of Representation" deliverable
    const sentLorDeliverable = lorDeliverables.find((d) =>
      (d.name || '').toLowerCase().includes('sent letter of representation'),
    );
    const deliverable = sentLorDeliverable ?? lorDeliverables[0];

    // Find the first match with a front_conversation_id (email thread)
    const matches = deliverable?.matches || [];
    const emailMatch = matches.find((m) => m.front_conversation_id);

    return emailMatch?.front_conversation_id ?? null;
  }, [lorDeliverables]);

  // Get carrier phone number for voice AI call
  const carrierPhone = useMemo(() => {
    const handles = coverage.carrier?.handles;
    if (!Array.isArray(handles) || handles.length === 0) return null;
    const phone = handles.find(
      (h) =>
        String(h?.handle_type || '')
          .toUpperCase()
          .includes('PHONE') ||
        String(h?.handle_type || '')
          .toUpperCase()
          .includes('VOICE'),
    );
    return phone?.value || null;
  }, [coverage.carrier?.handles]);

  // Get adjuster email for new email thread
  const adjusterEmail = useMemo(() => {
    const adjusters = coverage.adjusters;
    if (!Array.isArray(adjusters) || adjusters.length === 0) return null;
    // Look through all adjusters for an email handle
    for (const adjuster of adjusters) {
      if (!Array.isArray(adjuster.handles)) continue;
      const email = adjuster.handles.find((h) =>
        String(h?.handle_type || '')
          .toUpperCase()
          .includes('EMAIL'),
      );
      if (email?.value) return String(email.value);
    }
    return null;
  }, [coverage.adjusters]);

  // Get adjuster phone for dialpad calls
  const adjusterPhone = useMemo(() => {
    const adjusters = coverage.adjusters;
    if (!Array.isArray(adjusters) || adjusters.length === 0) return null;
    // Look through all adjusters for a phone handle
    for (const adjuster of adjusters) {
      if (!Array.isArray(adjuster.handles)) continue;
      const phone = adjuster.handles.find(
        (h) =>
          String(h?.handle_type || '')
            .toUpperCase()
            .includes('PHONE') ||
          String(h?.handle_type || '')
            .toUpperCase()
            .includes('VOICE'),
      );
      if (phone?.value) return String(phone.value);
    }
    return null;
  }, [coverage.adjusters]);

  const handleEdit = () => {
    if (!matterId) return;
    navigate(`/matters/${matterId}/coverage/${coverage.id}/edit`);
  };

  const handleMergeSelect = async (destinationId: string, sourceId: string) => {
    if (!matterId) return;
    try {
      await mergeCoverages({
        matterId,
        mergeCoverageRequest: {
          dst_coverage_id: destinationId,
          src_coverage_ids: [sourceId],
        },
      }).unwrap();
      toast.success('Coverages merged');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to merge coverages', err);
      toast.error('Merge failed');
    }
  };

  return (
    <Card
      className={cn(
        'bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden w-full transition-all duration-200 flex flex-col',
        className,
      )}
    >
      {/* Header */}
      <div className="px-3.5 py-2.5 border-b border-zinc-100">
        <div className="flex items-center justify-between">
          <div className={cn('flex items-center gap-2 min-w-0')}>
            <h4
              className={cn('font-semibold text-zinc-900 text-[14px] truncate')}
            >
              {onClick ? (
                <button
                  type="button"
                  aria-label={
                    isPremisesCategory
                      ? 'View property details'
                      : 'View coverage details'
                  }
                  className={cn(
                    'truncate text-left',
                    'cursor-pointer hover:underline underline-offset-2',
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                >
                  {isPremisesCategory
                    ? premisesDisplayName
                    : coverage.carrier?.name || 'Unknown Carrier'}
                </button>
              ) : (
                <span>
                  {isPremisesCategory
                    ? premisesDisplayName
                    : coverage.carrier?.name || 'Unknown Carrier'}
                </span>
              )}
            </h4>
            <span className="text-[10px] font-semibold tracking-wide text-zinc-600 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded">
              {policyTypeText}
            </span>
            {coverage.status !== 'APPROVED' ? (
              <span className="text-[10px] font-semibold tracking-wide text-zinc-600 bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded">
                {categoryLabel}
              </span>
            ) : null}
            {coverage.liability_status && (
              <CoverageLiabilityStatusBadge
                liabilityStatus={coverage.liability_status}
                denialReason={coverage.denial_reason}
                showLabel={false}
              />
            )}
            <Badge
              variant="secondary"
              className={cn(
                'text-[10px] px-1.5 py-0.5',
                getEnrichmentBadgeProps(coverage.enrichment_source).className,
              )}
            >
              {getEnrichmentBadgeProps(coverage.enrichment_source).text}
            </Badge>
            {carrierNameMismatch ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                    <AlertTriangle className="h-3 w-3" />
                    Mismatch
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Directory carrier &quot;{directoryCarrier?.name}&quot; differs
                  from coverage name &quot;
                  {coverageCarrierName || 'Unknown'}&quot;.
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <div className="flex items-center gap-1.5">
            {matterId ? (
              <CarrierPocketbook
                trigger={
                  <button
                    type="button"
                    className="flex items-center gap-1 h-6 px-2 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded transition-colors border border-violet-200 hover:border-violet-300"
                    title="Add Carrier Contact"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BookOpen className="w-3 h-3" />
                    <span className="font-medium">Add Contact</span>
                  </button>
                }
                defaultSelectedCarrierId={coverage.carrier_v2_id || undefined}
                onSelectDivision={async (carrier, selectedDivisionIdParam) => {
                  try {
                    if (!matterId) return;
                    await updateCoverage({
                      matterId,
                      coverageId: coverage.id,
                      coverage: {
                        id: coverage.id,
                        carrier_v2_id: carrier.id,
                        carrier_division_id: selectedDivisionIdParam,
                      },
                    }).unwrap();
                    toast.success('Carrier contact added');
                  } catch {
                    toast.error('Failed to add carrier contact');
                  }
                }}
                actions={['use']}
              />
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  className={cn(
                    'h-6 px-2 text-[10px] font-medium border',
                    getStatusButtonClasses(coverage.status),
                  )}
                  onClick={(e) => e.stopPropagation()}
                  disabled={!matterId}
                >
                  {getStatusLabel(coverage.status)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!matterId) return;
                    try {
                      await updateCoverage({
                        matterId,
                        coverageId: coverage.id,
                        coverage: { status: CoverageStatus.APPROVED },
                      }).unwrap();
                      toast.success('Coverage approved');
                    } catch {
                      toast.error('Failed to approve coverage');
                    }
                  }}
                >
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!matterId) return;
                    try {
                      await updateCoverage({
                        matterId,
                        coverageId: coverage.id,
                        coverage: { status: CoverageStatus.NEEDS_APPROVAL },
                      }).unwrap();
                      toast.success('Set to needs approval');
                    } catch {
                      toast.error('Failed to update');
                    }
                  }}
                >
                  Mark as Needs Approval
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!matterId) return;
                    try {
                      await updateCoverage({
                        matterId,
                        coverageId: coverage.id,
                        coverage: { status: CoverageStatus.REJECTED },
                      }).unwrap();
                      toast.success('Coverage rejected');
                    } catch {
                      toast.error('Failed to reject coverage');
                    }
                  }}
                >
                  Reject
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!matterId) return;
                    try {
                      await updateCoverage({
                        matterId,
                        coverageId: coverage.id,
                        coverage: { status: CoverageStatus.NOT_APPLICABLE },
                      }).unwrap();
                      toast.success('Coverage marked Not Applicable');
                    } catch {
                      toast.error('Failed to update coverage');
                    }
                  }}
                >
                  Mark as Not Applicable
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Open actions"
                  className="h-6 w-6 flex items-center justify-center rounded text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                <DropdownMenuItem
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!matterId) return;
                    try {
                      await updateCoverage({
                        matterId,
                        coverageId: coverage.id,
                        coverage: { status: CoverageStatus.APPROVED },
                      }).unwrap();
                      toast.success('Coverage approved');
                    } catch {
                      toast.error('Failed to approve coverage');
                    }
                  }}
                  disabled={!matterId}
                >
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick?.();
                  }}
                  disabled={!onClick}
                >
                  View details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  disabled={!matterId}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(coverage.id);
                  }}
                >
                  Delete
                </DropdownMenuItem>
                {(onMerge || matterId) && (allCoverages ?? []).length > 1 ? (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>Merge</DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-80 p-0">
                      {/* Auto Group */}
                      <div className="px-3 py-2 text-sm font-semibold text-gray-900">
                        Auto
                      </div>
                      {(allCoverages ?? [])
                        .filter((c) => c.id !== coverage.id)
                        .filter(
                          (c) =>
                            c.category === CoverageCategory.FIRST_PARTY ||
                            c.category === CoverageCategory.THIRD_PARTY,
                        )
                        .map((c) => (
                          <DropdownMenuItem
                            key={`auto-${c.id}`}
                            className="truncate"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onMerge) {
                                onMerge(coverage.id, c.id);
                              } else if (matterId) {
                                handleMergeSelect(coverage.id, c.id);
                              }
                            }}
                          >
                            {c.carrier?.name || 'Unknown Carrier'}
                          </DropdownMenuItem>
                        ))}

                      <div className="my-2 h-px bg-gray-200" />

                      {/* Health Group */}
                      <div className="px-3 py-2 text-sm font-semibold text-gray-900">
                        Health
                      </div>
                      {(allCoverages ?? [])
                        .filter((c) => c.id !== coverage.id)
                        .filter(
                          (c) =>
                            c.category === CoverageCategory.FIRST_PARTY_HEALTH,
                        )
                        .map((c) => (
                          <DropdownMenuItem
                            key={`health-${c.id}`}
                            className="truncate"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onMerge) {
                                onMerge(coverage.id, c.id);
                              } else if (matterId) {
                                handleMergeSelect(coverage.id, c.id);
                              }
                            }}
                          >
                            {c.carrier?.name || 'Unknown Carrier'}
                          </DropdownMenuItem>
                        ))}

                      <div className="my-2 h-px bg-gray-200" />

                      {/* Property Group */}
                      <div className="px-3 py-2 text-sm font-semibold text-gray-900">
                        Property
                      </div>
                      {(allCoverages ?? [])
                        .filter((c) => c.id !== coverage.id)
                        .filter(
                          (c) =>
                            c.category === CoverageCategory.PROPERTY,
                        )
                        .map((c) => (
                          <DropdownMenuItem
                            key={`property-${c.id}`}
                            className="truncate"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onMerge) {
                                onMerge(coverage.id, c.id);
                              } else if (matterId) {
                                handleMergeSelect(coverage.id, c.id);
                              }
                            }}
                          >
                            {c.named_insured?.[0] || c.carrier?.name || 'Unknown Property'}
                          </DropdownMenuItem>
                        ))}

                      <div className="my-2 h-px bg-gray-200" />

                      {/* Other Group */}
                      <div className="px-3 py-2 text-sm font-semibold text-gray-900">
                        Other
                      </div>
                      {(allCoverages ?? [])
                        .filter((c) => c.id !== coverage.id)
                        .filter(
                          (c) =>
                            c.category !== CoverageCategory.FIRST_PARTY &&
                            c.category !==
                              CoverageCategory.FIRST_PARTY_HEALTH &&
                            c.category !== CoverageCategory.THIRD_PARTY &&
                            c.category !== CoverageCategory.PROPERTY,
                        )
                        .map((c) => (
                          <DropdownMenuItem
                            key={`other-${c.id}`}
                            className="truncate"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onMerge) {
                                onMerge(coverage.id, c.id);
                              } else if (matterId) {
                                handleMergeSelect(coverage.id, c.id);
                              }
                            }}
                          >
                            {c.carrier?.name || 'Unknown Carrier'}
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* LOA Chase Status - shows when Review, Open Claim, LOR steps are done */}
      {showStepsPanel && canShowLOAChaser && matterId && lorTaskId && (
        <div className="px-3 pb-3 space-y-2">
          <LOAChaseStatusCard
            matterId={matterId}
            coverageId={coverage.id}
            taskId={lorTaskId}
            mode="compact"
          />
          <LegacyLOAChaserSection
            matterId={matterId}
            coverageId={coverage.id}
            carrierName={coverage.carrier?.name ?? undefined}
            suggestedLorThreadId={suggestedLorThreadId ?? undefined}
            claimNumber={primaryClaim?.claimNumber || ''}
            taskId={lorTaskId ?? undefined}
            defaultPhoneNumber={carrierPhone}
            defaultAdjusterPhone={adjusterPhone}
            defaultRecipientEmail={adjusterEmail}
          />
        </div>
      )}

      {/* Body - two columns with divider (50/50) */}
      {showStepsPanel ? (
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left column – can open details */}
          <div className={cn('p-3 space-y-1.5')}>
            {!(
              coverage.carrier?.handles && coverage.carrier.handles.length > 0
            ) && (
              <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5">
                <div className="text-[11px] font-semibold text-red-800">
                  Missing carrier contact
                </div>
                <div className="text-[10px] text-red-700 mt-0.5">
                  Use Carrier Directory to add contact handles.
                </div>
              </div>
            )}
            {coverage.named_insured && coverage.named_insured.length > 0 && (
              <div className="text-[12px] text-zinc-700">
                <span className="font-medium text-zinc-800">Insured:</span>{' '}
                <span className="break-all">{coverage.named_insured[0]}</span>
              </div>
            )}
            {primaryIdentifier && (
              <div className="text-[12px] text-zinc-700">
                <span className="font-medium text-zinc-800">
                  {primaryIdentifier.label}:
                </span>{' '}
                <span className="break-all">{primaryIdentifier.value}</span>
              </div>
            )}
            {primaryClaim && (
              <div className="text-[12px] text-zinc-700 flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-zinc-800">Claim #:</span>{' '}
                <span className="break-all font-mono text-[11px]">
                  {primaryClaim.claimNumber}
                </span>
                <DataSourceBadge
                  source={primaryClaim.source}
                  className="ml-0.5"
                />
              </div>
            )}
            {renderAdjusters(coverage.adjusters)}
            {renderCarrierContacts(coverage.carrier?.handles || [])}
            {renderPolicyLimits(coverage, onClick)}
          </div>
          {/* Right column – strictly task area; never navigates */}
          <div
            className={cn(
              'border-t md:border-t-0 md:border-l border-zinc-100',
              coverage.status === 'NOT_APPLICABLE' && 'bg-zinc-50',
            )}
          >
            {matterId ? (
              <CoverageStepRow matterId={matterId} coverage={coverage} />
            ) : null}
          </div>
        </div>
      ) : (
        <div className={cn('p-3 space-y-1.5')}>
          {!(
            coverage.carrier?.handles && coverage.carrier.handles.length > 0
          ) && (
            <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5">
              <div className="text-[11px] font-semibold text-red-800">
                Missing carrier contact
              </div>
              <div className="text-[10px] text-red-700 mt-0.5">
                Use Carrier Directory to add contact handles.
              </div>
            </div>
          )}
          {coverage.named_insured && coverage.named_insured.length > 0 && (
            <div className="text-[12px] text-zinc-700">
              <span className="font-medium text-zinc-800">Insured:</span>{' '}
              <span className="break-all">{coverage.named_insured[0]}</span>
            </div>
          )}
          {primaryIdentifier && (
            <div className="text-[12px] text-zinc-700">
              <span className="font-medium text-zinc-800">
                {primaryIdentifier.label}:
              </span>{' '}
              <span className="break-all">{primaryIdentifier.value}</span>
            </div>
          )}
          {primaryClaim && (
            <div className="text-[12px] text-zinc-700 flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-zinc-800">Claim #:</span>{' '}
              <span className="break-all font-mono text-[11px]">
                {primaryClaim.claimNumber}
              </span>
              <DataSourceBadge
                source={primaryClaim.source}
                className="ml-0.5"
              />
            </div>
          )}
          {renderAdjusters(coverage.adjusters)}
          {renderCarrierContacts(coverage.carrier?.handles || [])}
          {renderPolicyLimits(coverage, onClick)}
        </div>
      )}
    </Card>
  );
};
